import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, ExternalLink } from "lucide-react";
import { CountdownTimer } from "@/components/CountdownTimer";
import { ShareSection } from "@/components/ShareSection";
import { VipConfirmation } from "@/components/VipConfirmation";
import { VipBenefitsBox } from "@/components/VipBenefitsBox";
import {
  LOGO_URL,
  DUNDERTJEJ_URL,
  DUNDER_GUBBE_2026_URL,
} from "@/config/event";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const [formData, setFormData] = useState(() => ({
    name: "",
    email: "",
    ambassador_code: new URLSearchParams(window.location.search).get("ref")?.toUpperCase() || ""
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [codeValid, setCodeValid] = useState(null);
  const [ambassadorName, setAmbassadorName] = useState("");

  const validateCode = useCallback(async (code) => {
    if (!code || code.length < 2) {
      setCodeValid(null);
      setAmbassadorName("");
      return;
    }
    try {
      const response = await axios.get(`${API}/validate-code/${code}`);
      setCodeValid(response.data.valid);
      setAmbassadorName(response.data.valid ? response.data.ambassador : "");
    } catch {
      setCodeValid(false);
      setAmbassadorName("");
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateCode(formData.ambassador_code);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.ambassador_code, validateCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.ambassador_code) {
      toast.error("Fyll i alla fält");
      return;
    }

    if (!codeValid) {
      toast.error("Ogiltig ambassadör-kod");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/ticket-referral`, formData);
      setIsSuccess(true);
      toast.success("Grattis! Din registrering för VIP-band är bekräftad.");
    } catch (error) {
      const message = error.response?.data?.detail || "Något gick fel. Försök igen.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] relative overflow-hidden" role="main">
      {/* Background glow */}
      <div className="absolute inset-0 bg-gold-radial pointer-events-none" aria-hidden="true" />
      
      {/* Main content */}
      <article className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8 md:py-16">
        
        {/* Logo */}
        <header className="w-full max-w-[280px] md:max-w-[400px] mb-6 md:mb-10 opacity-0 animate-fade-in-up">
          <img 
            src={LOGO_URL} 
            alt="Dunderkryssningen - Sveriges största kryssningsfest"
            className="w-full drop-shadow-[0_0_15px_rgba(255,215,0,0.15)]"
            data-testid="main-logo"
            loading="eager"
            width="400"
            height="200"
          />
          <p
            className="-mt-2 text-center font-heading text-2xl uppercase leading-none text-white drop-shadow-[0_3px_8px_rgba(255,255,255,0.18)] sm:text-3xl md:-mt-3 md:text-4xl"
            data-testid="event-date"
            aria-label="13 till 15 november"
          >
            13–15 NOVEMBER
          </p>
        </header>

        {/* Hero Text */}
        <section className="text-center mb-6 md:mb-8 opacity-0 animate-fade-in-up animate-delay-100" aria-labelledby="main-headline">
          <h1 
            id="main-headline"
            className="font-heading text-3xl sm:text-4xl md:text-6xl text-white uppercase tracking-tight leading-none mb-3"
            data-testid="main-headline"
          >
            Köp Biljett
          </h1>
          <p className="text-gray-400 text-sm md:text-base max-w-md mx-auto">
            Fyll i dina uppgifter och din ambassadör-kod
          </p>
        </section>

        {/* Countdown */}
        <div className="mb-8 md:mb-10 opacity-0 animate-fade-in-up animate-delay-100">
          <CountdownTimer />
        </div>

        {/* Content wrapper with VIP box and form */}
        <div className="w-full max-w-4xl flex flex-col items-center justify-center gap-6">
          <VipBenefitsBox />
          
          {/* Form Container - Center */}
          <section className="w-full max-w-md relative opacity-0 animate-fade-in-up animate-delay-200" aria-label="Biljettformulär">
            {/* Mascot - Dundertjej (right side) */}
            <img 
              src={DUNDERTJEJ_URL}
              alt="Dunderkryssningen maskot"
              className="absolute -right-8 -top-16 w-24 sm:w-28 md:w-44 md:-right-32 md:-top-10 z-0 opacity-80 sm:opacity-90 animate-float pointer-events-none"
              data-testid="mascot-dundertjej"
              loading="lazy"
              aria-hidden="true"
            />
            
            {/* Mascot - Dunder 2026 Gubbe (left side) */}
            <img 
              src={DUNDER_GUBBE_2026_URL}
              alt="Dunderkryssningen 2026 maskot"
              className="absolute -left-8 top-20 w-24 sm:w-28 md:w-36 md:-left-32 md:top-10 z-0 opacity-80 sm:opacity-90 animate-float-slow pointer-events-none hidden sm:block"
              data-testid="mascot-gubbe-2026"
              loading="lazy"
              aria-hidden="true"
            />

            {/* Form Card */}
            <div 
              className={`relative z-10 bg-[#0A0A0A] border border-[#222] p-6 md:p-8 ${isSuccess ? 'animate-success' : ''}`}
              data-testid="ticket-form-container"
            >
              {!isSuccess ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label 
                      htmlFor="name"
                      className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left"
                    >
                      Namn
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Ditt fullständiga namn"
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                      data-testid="input-name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label 
                      htmlFor="email"
                      className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left"
                    >
                      E-post
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="din@email.se"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                      data-testid="input-email"
                    />
                  </div>

                  {/* Ambassador Code */}
                  <div>
                    <label 
                      htmlFor="ambassador_code"
                      className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left"
                    >
                      Ambassadör-kod
                    </label>
                    <div className="relative">
                      <Input
                        id="ambassador_code"
                        name="ambassador_code"
                        type="text"
                        placeholder="Ange din kod"
                        value={formData.ambassador_code}
                        onChange={handleChange}
                        className={`bg-[#1A1A1A] border-2 text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm uppercase ${
                          codeValid === true ? 'border-green-500' : 
                          codeValid === false ? 'border-red-500' : 
                          'border-transparent'
                        }`}
                        data-testid="input-ambassador-code"
                      />
                      {codeValid === true && ambassadorName && (
                        <div className="mt-2 text-green-400 text-sm flex items-center gap-1" data-testid="valid-code-message">
                          <CheckCircle className="w-4 h-4" />
                          Ambassadör: {ambassadorName}
                        </div>
                      )}
                      {codeValid === false && formData.ambassador_code && (
                        <div className="mt-2 text-red-400 text-sm" data-testid="invalid-code-message">
                          Ogiltig kod
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting || !codeValid}
                    className="w-full bg-[#FFD700] text-black font-bold uppercase tracking-widest hover:bg-[#E5C100] hover:scale-[1.02] transition-all duration-300 h-14 rounded-sm gold-glow gold-glow-hover text-base mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Skickar...
                      </>
                    ) : (
                      <>
                        Gå till biljettköp
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <VipConfirmation name={formData.name} email={formData.email} />
              )}
            </div>

            {/* Ambassador link */}
            <div className="mt-6 text-center">
              <Link 
                to="/ambassador" 
                className="inline-block px-6 py-3 border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 hover:border-[#FFD700] transition-all duration-300 text-sm uppercase tracking-wider rounded-sm"
                data-testid="ambassador-link"
              >
                Vill du bli Ambassadör?
              </Link>
            </div>
          </section>
          
          {/* Divider */}
          <div className="w-full max-w-md flex items-center gap-4 opacity-0 animate-fade-in-up animate-delay-400" aria-hidden="true">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#FFD700]/30" />
            <span className="text-[#FFD700]/40 text-xs">◆</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#FFD700]/30" />
          </div>

          {/* Share Section */}
          <div className="w-full flex justify-center opacity-0 animate-fade-in-up animate-delay-400 pt-2">
            <ShareSection />
          </div>
        </div>
      </article>
    </main>
  );
}
